/**
 * FactoryAreaLocationPage — Master data card "Area/Location" trong Factory Data
 * hub (`/factory/<code>/data` → card Area/Location → hidden tab `area-location`).
 *
 * List = bảng `public.factory_area` scope theo factory hiện tại (cột
 * `factory_schema`). Flat CRUD: New / Edit / Copy / Delete (soft-delete
 * is_status=1). KHÔNG có tài liệu Access form (factory_area là reference table
 * dùng làm RowSource) → build mới theo convention web, bố cục bám
 * FactoryDeductionRatePage.
 */
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { useMemo, useState, type CSSProperties } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  DataframeBody,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useIsMobile } from '@/hooks/useResponsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import {
  useCreateFactoryAreaMutation,
  useDeleteFactoryAreaMutation,
  useGetFactoryAreaListQuery,
  useUpdateFactoryAreaMutation,
  type FactoryAreaPayload,
  type FactoryAreaRow,
} from '@/store/api/factoryApi'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'
import {
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '@/theme/responsive'

import AreaLocationFormDialog, {
  type AreaLocationDialogMode,
} from './AreaLocationFormDialog'

const AREA_ACCENT = '#2e6e6c'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

interface ColumnDef {
  key: string
  label: string
  kind?: 'number' | 'datetime'
  align?: 'right'
  filter?: ColumnFilterKind
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'full_area_code', label: 'Full Area Code' },
  { key: 'area_code', label: 'Area Code' },
  { key: 'stock', label: 'Warehouse (Stock)' },
  { key: 'square_m2', label: 'Square m²', kind: 'number', align: 'right' },
  { key: 'note', label: 'Note' },
  { key: 'created_date', label: 'Created Date', kind: 'datetime' },
  { key: 'updated_date', label: 'Updated Date', kind: 'datetime' },
  { key: 'created_by', label: 'Created By' },
  { key: 'updated_by', label: 'Updated By' },
]

/** Cột freeze khi scroll ngang — cột định danh row. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['full_area_code'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { full_area_code: 160 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { full_area_code: 140 }

const formatNumberCell = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// DD-MMM-YYYY HH:mm (convention Access, khớp các list Factory khác).
const formatDateTime = (value: unknown): string => {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const cellValue = (row: FactoryAreaRow, col: ColumnDef): string => {
  const v = (row as unknown as Record<string, unknown>)[col.key]
  if (v == null || v === '') return ''
  if (col.kind === 'number') return formatNumberCell(v)
  if (col.kind === 'datetime') return formatDateTime(v)
  return String(v)
}

interface FactoryAreaLocationPageProps {
  factoryCode: string
  onBackToHub: () => void
  canEdit: boolean
}

type DialogState =
  | { kind: 'closed' }
  | { kind: AreaLocationDialogMode; initial: FactoryAreaRow | null }

const ALL_ROWS_PAGE_SIZE = -1

export default function FactoryAreaLocationPage({
  factoryCode,
  onBackToHub,
  canEdit,
}: FactoryAreaLocationPageProps) {
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP

  const list = useGetFactoryAreaListQuery({ factoryCode })
  const [createArea, { isLoading: creating }] = useCreateFactoryAreaMutation()
  const [updateArea, { isLoading: updating }] = useUpdateFactoryAreaMutation()
  const [deleteArea, { isLoading: deleting }] = useDeleteFactoryAreaMutation()
  const saving = creating || updating

  const {
    selectedId,
    setSelectedId,
    toggleSelect,
  } = useRowSelection<number>()
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const [pendingDelete, setPendingDelete] = useState<FactoryAreaRow | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(ALL_ROWS_PAGE_SIZE)

  const { getWidth, startResize } = useColumnWidths('factory.areaLocation.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'factory',
    prefKey: 'columns',
    subKey: 'area-location',
    allColumns: ALL_COLUMNS,
    widthsTableKey: 'factory.areaLocation.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const baseRows = useMemo<FactoryAreaRow[]>(() => list.data?.items ?? [], [list.data])

  const stockOptions = useMemo<string[]>(() => {
    const set = new Set<string>()
    for (const r of baseRows) {
      const s = (r.stock ?? '').trim()
      if (s) set.add(s)
    }
    return [...set].sort((a, b) => a.localeCompare(b))
  }, [baseRows])

  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm, ALL_COLUMNS)) return false
        if (!rowMatchesAllColumnFilters(row, columnFilters.filters)) return false
        return true
      }),
    [baseRows, searchTerm, columnFilters.filters],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? ALL_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, ALL_COLUMNS),
    [filteredRows, sortState],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const autoColWidths = useMemo<Record<string, number>>(() => {
    const CHAR_PX = 7.7,
      HEADER_PAD = 52,
      CELL_PAD = 28,
      MIN = 100,
      MAX = 320
    const sample = filteredRows.slice(0, 400)
    const out: Record<string, number> = {}
    for (const col of visibleColumns) {
      let bodyMax = 0
      for (const r of sample) {
        const len = cellValue(r, col).length
        if (len > bodyMax) bodyMax = len
      }
      const headerPx = (col.label ?? col.key).length * CHAR_PX + HEADER_PAD
      const bodyPx = bodyMax * CHAR_PX + CELL_PAD
      const minForType = col.kind === 'datetime' ? 150 : MIN
      out[col.key] = Math.round(Math.min(MAX, Math.max(minForType, headerPx, bodyPx)))
    }
    return out
  }, [visibleColumns, filteredRows])

  const minTableWidth = useMemo(() => {
    let sum = 0
    for (const col of visibleColumns) {
      const k = col.key
      sum += getWidth(k) ?? (FROZEN_KEYS.has(k) ? FROZEN_FALLBACK_WIDTH[k] : autoColWidths[k]) ?? 96
    }
    return Math.max(720, sum)
  }, [visibleColumns, getWidth, FROZEN_FALLBACK_WIDTH, autoColWidths])
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: string) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo(
    () => baseRows.find((r) => r.id === selectedId) ?? null,
    [baseRows, selectedId],
  )

  const requireRow = (): FactoryAreaRow | null => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return null
    }
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }

  const handleNewClick = () => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    setDialog({ kind: 'new', initial: null })
  }
  const handleEditClick = () => {
    const row = requireRow()
    if (row) setDialog({ kind: 'edit', initial: row })
  }
  const handleCopyClick = () => {
    const row = requireRow()
    if (row) setDialog({ kind: 'copy', initial: row })
  }
  const handleDeleteClick = () => {
    const row = requireRow()
    if (row) setPendingDelete(row)
  }

  const handleSave = async (payload: FactoryAreaPayload) => {
    if (!canEdit || dialog.kind === 'closed') return
    try {
      if (dialog.kind === 'edit' && dialog.initial) {
        await updateArea({ factoryCode, areaId: dialog.initial.id, body: payload }).unwrap()
        snack.success('Area/Location saved')
      } else {
        await createArea({ factoryCode, body: payload }).unwrap()
        snack.success('Area/Location added')
      }
      setDialog({ kind: 'closed' })
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Save failed')
          : 'Save failed'
      snack.error(msg)
    }
  }

  const handleConfirmDelete = async () => {
    if (!canEdit || !pendingDelete) return
    try {
      await deleteArea({ factoryCode, areaId: pendingDelete.id }).unwrap()
      snack.success('Area/Location deleted')
      if (selectedId === pendingDelete.id) setSelectedId(null)
      setPendingDelete(null)
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Delete failed')
          : 'Delete failed'
      snack.error(msg)
    }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
            onClick={onBackToHub}
            title="Datasets"
            sx={filterBarBtnSx}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Datasets</Box>
          </Button>
          <Stack
            direction="row"
            spacing={0.625}
            alignItems="center"
            sx={{
              mx: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: AREA_ACCENT,
              color: '#fff',
              boxShadow: `0 1px 3px ${alpha(AREA_ACCENT, 0.45)}`,
            }}
          >
            <WarehouseIcon sx={{ fontSize: '0.95rem', color: '#fff' }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                fontSize: '0.85rem',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
                ...HIDE_ON_MOBILE_INLINE,
              }}
            >
              AREA / LOCATION
            </Typography>
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400, ...HIDE_ON_MOBILE_INLINE }}>
            Filter
          </Typography>
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Tooltip title={canEdit ? 'Add new area/location' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="contained"
                size="small"
                color="success"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || saving}
                onClick={handleNewClick}
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                title="New"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Edit selected area/location' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !selectedRow || saving}
                onClick={handleEditClick}
                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                title="Edit"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Copy selected as a new area/location' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                color="info"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !selectedRow || saving}
                onClick={handleCopyClick}
                startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                title="Copy"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canEdit ? 'Delete selected area/location' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || !selectedRow || deleting}
                onClick={handleDeleteClick}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {list.isError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load area/location list.
        </Alert>
      ) : null}

      <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0, height: 'auto' }}>
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(list.isLoading || list.isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Area code, warehouse, note..."
              label="Search Area/Location"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={list.isLoading} fetching={list.isFetching} sx={{ minHeight: { xs: 280, sm: 320 } }}>
          {list.isError ? (
            <BrandState
              tone="error"
              title="Could not load area/location list"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : list.isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No area/location records found"
              description="Use New to add one, or adjust the search/filter above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : autoColWidths[col.key])
                    const headerStyle: CSSProperties = {}
                    if (w) {
                      headerStyle.width = w
                      headerStyle.minWidth = w
                      headerStyle.maxWidth = w
                    }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: FactoryAreaRow) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const isFrozen = FROZEN_KEYS.has(col.key)
                  const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : autoColWidths[col.key])
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-wrap">
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  const isSelected = row != null && row.id === selectedId
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (row != null) toggleSelect(row.id)
                      }}
                      onDoubleClick={() => {
                        if (!canEdit || row == null) return
                        setDialog({ kind: 'edit', initial: row })
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <AreaLocationFormDialog
        open={dialog.kind !== 'closed'}
        mode={dialog.kind === 'closed' ? 'new' : dialog.kind}
        saving={saving}
        canEdit={canEdit}
        factoryCode={factoryCode}
        initial={dialog.kind === 'closed' ? null : dialog.initial}
        stockOptions={stockOptions}
        onClose={() => setDialog({ kind: 'closed' })}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={pendingDelete != null}
        title="Delete Area/Location"
        message={
          pendingDelete
            ? `Delete area/location "${pendingDelete.full_area_code ?? ''}"?`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
